import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

const categoryMap: Record<string, { value: string; label: string; icon: string }> = {
  자유게시판: { value: "community", label: "자유게시판", icon: "ri-chat-smile-3-line" },
  홍보센터: { value: "promote", label: "홍보센터", icon: "ri-megaphone-line" },
  월드컵픽스터: { value: "tip", label: "월드컵픽스터", icon: "ri-football-line" },
  먹튀제보: { value: "scam_report", label: "먹튀제보", icon: "ri-error-warning-line" },
};

const categories = Object.values(categoryMap);

const POST_POINT_REWARD = 10;

export default function Write() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetCategory = searchParams.get("category") || "";
  const { user, loading: authLoading } = useAuth();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedLabel, setSelectedLabel] = useState(presetCategory || "자유게시판");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pointAwarded, setPointAwarded] = useState(false);
  const [awardMessage, setAwardMessage] = useState("");

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-foreground-500">
          <span className="w-4 h-4 flex items-center justify-center animate-spin">
            <i className="ri-loader-4-line" />
          </span>
          <span className="text-sm">로딩 중...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
              <i className="ri-lock-line text-2xl" />
            </span>
          </div>
          <h2 className="text-lg font-bold text-foreground-700 mb-2">로그인이 필요합니다</h2>
          <p className="text-sm text-foreground-600 mb-4">글쓰기는 로그인 후 이용할 수 있습니다</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            로그인하러 가기
          </Link>
        </div>
      </div>
    );
  }

  const awardPostPoints = async () => {
    try {
      // Ensure profile exists
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id, points")
        .eq("id", user.id)
        .maybeSingle();

      let currentPoints = existingProfile?.points ?? 0;

      if (!existingProfile) {
        // Create profile if missing
        const { error: createErr } = await supabase
          .from("profiles")
          .insert({ id: user.id, points: POST_POINT_REWARD });

        if (createErr) {
          console.warn("Profile creation failed:", createErr.message);
          return;
        }
        currentPoints = POST_POINT_REWARD;
      } else {
        await supabase
          .from("profiles")
          .update({ points: currentPoints + POST_POINT_REWARD })
          .eq("id", user.id);
        currentPoints = currentPoints + POST_POINT_REWARD;
      }

      await supabase
        .from("point_transactions")
        .insert({
          user_id: user.id,
          type: "적립",
          detail: "게시글 작성 보상",
          amount: POST_POINT_REWARD,
        });

      setPointAwarded(true);
      setAwardMessage(`게시글 작성 보상으로 ${POST_POINT_REWARD}P가 적립되었습니다! (총 ${currentPoints}P)`);
    } catch {
      // Silently fail - point award is bonus
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("제목을 입력해주세요");
      return;
    }

    if (!content.trim()) {
      setError("내용을 입력해주세요");
      return;
    }

    if (content.length > 5000) {
      setError("내용은 5000자 이내로 작성해주세요");
      return;
    }

    setSubmitting(true);

    const dbCategory = categoryMap[selectedLabel]?.value || "community";

    try {
      const { data, error: insertError } = await supabase
        .from("posts")
        .insert({
          title: title.trim(),
          content: content.trim(),
          category: dbCategory,
          author_id: user.id,
          views: 0,
          likes: 0,
        })
        .select("id")
        .single();

      if (insertError) {
        setError("게시글 작성 중 오류가 발생했습니다: " + insertError.message);
        setSubmitting(false);
        return;
      }

      // Award points after successful post creation
      await awardPostPoints();

      if (data) {
        navigate(`/post/${data.id}`, { replace: true });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "알 수 없는 오류";
      setError("게시글 작성 중 오류가 발생했습니다: " + msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[800px] mx-auto px-3 md:px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 border border-background-300 text-foreground-600 hover:text-foreground-400 transition-colors"
            >
              <i className="ri-arrow-left-line" />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-foreground-700">글쓰기</h1>
              <p className="text-xs text-foreground-600 mt-0.5">새로운 게시글을 작성해보세요</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-accent-100/80 rounded-lg">
            <span className="w-4 h-4 flex items-center justify-center text-accent-500">
              <i className="ri-coin-line" />
            </span>
            <span className="text-xs text-accent-700 font-medium">글쓰기 +{POST_POINT_REWARD}P</span>
          </div>
        </div>

        <div className="bg-background-100 rounded-xl border border-background-300 p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            {pointAwarded && (
              <div className="bg-accent-100/80 border border-accent-300/60 rounded-lg px-4 py-3 text-sm text-accent-700 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-accent-500 flex-shrink-0">
                  <i className="ri-checkbox-circle-fill" />
                </span>
                <span>{awardMessage}</span>
              </div>
            )}

            {/* Category Select */}
            <div>
              <label className="block text-xs text-foreground-600 mb-2">카테고리</label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.label}
                    type="button"
                    onClick={() => setSelectedLabel(cat.label)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-colors cursor-pointer ${
                      selectedLabel === cat.label
                        ? "border-primary-500 bg-primary-500/10 text-primary-400"
                        : "border-background-300 bg-background-200 hover:border-foreground-600 text-foreground-600"
                    }`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={cat.icon} />
                    </span>
                    <span className="text-[10px] font-medium whitespace-nowrap">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs text-foreground-600 mb-1.5">제목</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목을 입력하세요"
                maxLength={200}
                className="w-full bg-background-200 rounded-lg px-4 py-3 text-sm text-foreground-700 placeholder-foreground-500 border border-background-300 focus:border-primary-500/50 outline-none transition-colors"
              />
              <p className="text-[10px] text-foreground-600 mt-1 text-right">{title.length}/200</p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-xs text-foreground-600 mb-1.5">내용</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="내용을 입력하세요"
                rows={12}
                maxLength={5000}
                className="w-full bg-background-200 rounded-lg px-4 py-3 text-sm text-foreground-700 placeholder-foreground-500 border border-background-300 focus:border-primary-500/50 outline-none transition-colors resize-y min-h-[240px]"
              />
              <p className="text-[10px] text-foreground-600 mt-1 text-right">{content.length}/5000</p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-background-300">
              <Link
                to="/"
                className="px-5 py-2.5 text-sm text-foreground-600 hover:text-foreground-400 bg-background-200 hover:bg-background-300 rounded-lg transition-colors whitespace-nowrap"
              >
                취소
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {submitting ? "등록 중..." : "등록하기"}
              </button>
            </div>

            {/* honeypot */}
            <div className="hp-field">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}